import React from 'react';
import {
  FaUserTie,
  FaUsers,
  FaHourglassHalf,
  FaCheckDouble,
  FaCalendarCheck,
  FaProjectDiagram,
  FaBuilding,
  FaMapMarkerAlt,
  FaExclamationTriangle,
  FaPaperPlane,
  FaFileAlt,
} from 'react-icons/fa';
import { StatCard, SectionCard, MiniStat, DataTable } from './ui';
import { ApprovalsSection } from './approvals-section';
import { BarChart } from './charts';

/**
 * Project Manager — NEW spec sections ("Dashboard 140626 Mukul", PM tab).
 *
 * COMPANION component (not a replacement). Render alongside the existing
 * <ProjectManagerView> in Dashboard.tsx:
 *
 *   case ROLE_IDS.PROJECT_MANAGER:
 *     return (
 *       <>
 *         {dashboardData.my_attendance && <PersonalDashboard ... />}
 *         <ProjectManagerSpecSections data={dashboardData} />
 *         <ProjectManagerView data={dashboardData as unknown as ProjectManagerDashboard} />
 *       </>
 *     );
 *
 * Keep this file in components/role-dashboards so './ui' and './charts' resolve.
 * Reads the sections added by getProjectManagerSpecSections; renders nothing for
 * any section the backend hasn't supplied.
 */

const toNumber = (v: unknown) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

// Pending workflow-stage labels (row 33)
const PENDING_LABELS: Record<string, string> = {
  site_created: 'Site Created',
  engineer_assigned: 'Engineer Assigned',
  field_data_upload_pending: 'Field Data Upload Pending',
  ta_review_pending: 'TA Review Pending',
  mis_report_assign_pending: 'MIS Report Assign Pending',
  report_in_progress: 'Report In Progress',
  validation_pending: 'Validation Pending',
  report_finalized: 'Report Finalized',
};

// Pending grouped raw-row statuses (activity/project level come back as flat enum rows)
const PENDING_RAW_LABELS: Record<string, string> = {
  site_created: 'Site Created',
  engineer_assigned: 'Engineer Assigned',
  field_work_completed: 'Field Data Upload Pending',
  ta_review_pending: 'TA Review Pending',
  mis_report_assign_pending: 'MIS Report Assign Pending',
  report_in_progress: 'Report In Progress',
  report_completed: 'Validation Pending',
  report_finalized: 'Report Finalized',
};
const PENDING_RAW_STATUSES = Object.keys(PENDING_RAW_LABELS);

// Pre-pivoted progress output keys → labels (rows 58/83)
const PROGRESS_LABELS: Record<string, string> = {
  field_work_in_progress: 'Field Work In Progress',
  site_data_wip: 'Site Data WIP',
  field_work_completed: 'Survey Done',
  ta_review_completed: 'Field Work Approved',
  report_completed: 'Report Completed',
  report_finalized: 'Report Finalized',
  report_sent_to_client: 'Report Sent',
};
const PROGRESS_KEYS = Object.keys(PROGRESS_LABELS);

const REJECT_LABELS: Record<string, string> = {
  ta_rejected: 'TA Rejected',
  report_maker_rejected: 'Report Maker Rejected',
  validation_rejected: 'Validation Rejected',
};
const REJECT_STATUSES = Object.keys(REJECT_LABELS);

// pivot flat grouped rows ({ '<name>': x, survey_workflow_status, count }) → one row per dim
const pivotLevel = (rows: any[] | undefined, nameKeys: string[], statuses: string[]) => {
  const map = new Map<string, { name: string } & Record<string, number>>();
  for (const r of rows || []) {
    const name = nameKeys.map((k) => r?.[k]).find((v) => v != null && v !== '') || 'Unassigned';
    const key = String(name);
    const existing = map.get(key) || ({ name: key } as { name: string } & Record<string, number>);
    const st = String(r?.survey_workflow_status ?? '');
    existing[st] = (existing[st] || 0) + toNumber(r?.count);
    map.set(key, existing);
  }
  return Array.from(map.values()).map((row) => {
    statuses.forEach((s) => { if (row[s] == null) row[s] = 0; });
    return row;
  });
};

const flatCols = (firstLabel: string, keys: string[], labels: Record<string, string>) => [
  { key: 'name', label: firstLabel },
  ...keys.map((s) => ({ key: s, label: labels[s] ?? s, align: 'center' as const })),
];
const pivotedCols = (firstKey: string, firstLabel: string, keys: string[], labels: Record<string, string>) => [
  { key: firstKey, label: firstLabel },
  ...keys.map((s) => ({ key: s, label: labels[s] ?? s, align: 'center' as const })),
];

/**
 * Where each dashboard number came from.
 *
 * Every tile links to the list page that produced it, carrying the same
 * filters — clicking "Leaves Applied: 5" lands on the leave list already
 * narrowed to the five pending leaves. The list pages read these params on
 * mount via `useUrlFilters`.
 *
 * Workflow-status groups repeat the exact status sets the API buckets by
 * (analytics.controller.ts, "Group-stage buckets"); if a bucket changes there
 * it has to change here too, or the tile and its list will disagree.
 */
const STAGE_GROUPS = {
  survey_pending: ['site_created', 'engineer_assigned', 'field_work_in_progress'],
  field_data_upload_pending: ['data_uploaded', 'field_work_completed', 'ta_review_pending'],
  report_preparation_pending: ['mis_report_assign_pending', 'report_maker_assigned', 'report_in_progress'],
  validation_pending: ['report_completed', 'validator_assigned', 'validation_in_progress'],
  report_submission_pending: ['report_finalized'],
} as const;

const query = (params: Record<string, string | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

/** Sites at any of a group's workflow statuses. */
const sitesAtStage = (group: keyof typeof STAGE_GROUPS) =>
  `/sites${query({ workflow_status_list: STAGE_GROUPS[group].join(',') })}`;

/** First and last day of the month a YYYY-MM-DD date falls in. */
const monthRange = (date: string) => {
  const [year, month] = date.split('-').map(Number);
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, '0');
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}` };
};

interface Props {
  data: any;
}

export const ProjectManagerSpecSections: React.FC<Props> = ({ data }) => {
  const d = data || {};
  const responsibility = d.responsibility_overview;
  const approvals = d.approvals;
  const pendingWork = d.pending_work;
  const todayProgress = d.today_progress;
  const yesterdayProductivity = d.yesterday_productivity;
  const rejectionPending = d.rejection_pending;
  const referenceLists = d.reference_lists;

  // The API reports in IST; match it so a link never asks for the wrong day.
  const today = todayProgress?.date || new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const month = monthRange(today);

  const pendingActivity = pivotLevel(pendingWork?.activity_level, ['activity.activity_name'], PENDING_RAW_STATUSES);
  const pendingProject = pivotLevel(pendingWork?.project_level, ['project.project_short_name', 'project.project_name'], PENDING_RAW_STATUSES);
  const rejectActivity = pivotLevel(rejectionPending?.activity_level, ['activity.activity_name'], REJECT_STATUSES);
  const rejectProject = pivotLevel(rejectionPending?.project_level, ['project.project_short_name', 'project.project_name'], REJECT_STATUSES);

  // pending user-level rows arrive as flat enum rows per role → pivot each
  const pendingEngineer = pivotLevel(pendingWork?.user_level?.engineer_wise, ['site_engineer_username'], ['engineer_assigned', 'field_work_in_progress', 'data_uploaded', 'field_work_completed']);
  const pendingTc = pivotLevel(pendingWork?.user_level?.tc_wise, ['team_coordinator_username'], ['site_created', 'ta_review_pending']);
  const pendingTa = pivotLevel(pendingWork?.user_level?.ta_wise, ['ta_username'], ['ta_review_pending']);
  const pendingMis = pivotLevel(pendingWork?.user_level?.mis_wise, ['mis_username'], ['mis_report_assign_pending', 'report_in_progress', 'report_completed']);

  const renderProgress = (block: any, title: string, icon: any) => {
    if (!block) return null;
    const gs = block.group_stage || {};
    const ul = block.user_level || {};
    return (
      <div className="space-y-6">
        <SectionCard title={`${title} — Group Stage`} subtitle={block.date} icon={icon}>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <MiniStat label="Survey Done" value={gs.survey_done ?? 0} color="blue" />
            <MiniStat label="Field Work Approved" value={gs.field_work_approved ?? 0} color="green" />
            <MiniStat label="Report Completed" value={gs.report_completed ?? 0} color="yellow" />
            <MiniStat label="Report Finalized" value={gs.report_finalized ?? 0} color="green" />
            <MiniStat label="Report Sent" value={gs.report_sent ?? 0} color="green" />
          </div>
        </SectionCard>

        {((block.activity_level?.length ?? 0) > 0 || (block.project_level?.length ?? 0) > 0) && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {(block.activity_level?.length ?? 0) > 0 && (
              <SectionCard title={`${title} — Activity Level`} icon={FaProjectDiagram}>
                <DataTable columns={pivotedCols('activity_name', 'Activity', PROGRESS_KEYS, PROGRESS_LABELS)} data={block.activity_level} maxHeight="300px" emptyMessage="No progress" />
              </SectionCard>
            )}
            {(block.project_level?.length ?? 0) > 0 && (
              <SectionCard title={`${title} — Project Level`} icon={FaBuilding}>
                <DataTable columns={pivotedCols('project_name', 'Project', PROGRESS_KEYS, PROGRESS_LABELS)} data={block.project_level} maxHeight="300px" emptyMessage="No progress" />
              </SectionCard>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(ul.engineer_wise?.length ?? 0) > 0 && (
            <SectionCard title={`${title} — Engineer Level`} icon={FaMapMarkerAlt}>
              <DataTable columns={pivotedCols('username', 'Engineer', ['field_work_in_progress', 'site_data_wip', 'field_work_completed'], PROGRESS_LABELS)} data={ul.engineer_wise} maxHeight="260px" emptyMessage="None" />
            </SectionCard>
          )}
          {(ul.tc_wise?.length ?? 0) > 0 && (
            <SectionCard title={`${title} — TC Level`} icon={FaUserTie}>
              <DataTable columns={pivotedCols('username', 'TC', ['site_created', 'engineer_assigned', 'ta_review_completed'], PROGRESS_LABELS)} data={ul.tc_wise} maxHeight="260px" emptyMessage="None" />
            </SectionCard>
          )}
          {(ul.ta_wise?.length ?? 0) > 0 && (
            <SectionCard title={`${title} — TA Level`} icon={FaUserTie}>
              <DataTable columns={pivotedCols('username', 'TA', ['ta_review_completed'], PROGRESS_LABELS)} data={ul.ta_wise} maxHeight="260px" emptyMessage="None" />
            </SectionCard>
          )}
          {(ul.mis_wise?.length ?? 0) > 0 && (
            <SectionCard title={`${title} — MIS Level`} icon={FaUserTie}>
              <DataTable columns={pivotedCols('username', 'MIS', ['report_completed', 'report_finalized', 'report_sent_to_client'], PROGRESS_LABELS)} data={ul.mis_wise} maxHeight="260px" emptyMessage="None" />
            </SectionCard>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Responsibility Overview */}
      {responsibility && (
        <SectionCard title="Responsibility Overview" icon={FaProjectDiagram}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatCard to="/projects" title="Assigned Projects" value={responsibility.assigned_projects ?? 0} icon={FaProjectDiagram} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
            <StatCard to="/team" title="Direct Team" value={responsibility.direct_team ?? 0} icon={FaUsers} iconBgColor="bg-indigo-100" iconColor="text-indigo-600" />
            <StatCard to="/team" title="All Team" value={responsibility.all_team ?? 0} icon={FaUsers} iconBgColor="bg-violet-100" iconColor="text-violet-600" />
            <StatCard to={`/sites${query({ field_work_completed_from: month.from, field_work_completed_to: month.to })}`} title="Survey Done (Month)" value={responsibility.survey_done_current_month ?? 0} icon={FaCheckDouble} iconBgColor="bg-green-100" iconColor="text-green-600" />
            <StatCard to={`/sites${query({ sent_to_client_from: month.from, sent_to_client_to: month.to })}`} title="Report Sent (Month)" value={responsibility.report_sent_current_month ?? 0} icon={FaPaperPlane} iconBgColor="bg-emerald-100" iconColor="text-emerald-600" />
            <StatCard to={sitesAtStage('survey_pending')} title="Survey Pending" value={responsibility.survey_pending ?? 0} icon={FaHourglassHalf} iconBgColor="bg-orange-100" iconColor="text-orange-600" />
            <StatCard to={sitesAtStage('report_submission_pending')} title="Report Submission Pending" value={responsibility.report_submission_pending ?? 0} icon={FaFileAlt} iconBgColor="bg-teal-100" iconColor="text-teal-600" />
          </div>
        </SectionCard>
      )}

      {/* Approvals */}
      <ApprovalsSection approvals={approvals} />

      {/* Pending Work — group stage + workflow bar */}
      {pendingWork && (
        <SectionCard title="Pending Work — Group Stage" icon={FaHourglassHalf}>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <MiniStat to={sitesAtStage('survey_pending')} label="Survey Pending" value={pendingWork.group_stage?.survey_pending ?? 0} color="blue" />
            <MiniStat to={sitesAtStage('field_data_upload_pending')} label="Field Data Upload Pending" value={pendingWork.group_stage?.field_data_upload_pending ?? 0} color="yellow" />
            <MiniStat to={sitesAtStage('report_preparation_pending')} label="Report Preparation Pending" value={pendingWork.group_stage?.report_preparation_pending ?? 0} color="yellow" />
            <MiniStat to={sitesAtStage('validation_pending')} label="Validation Pending" value={pendingWork.group_stage?.validation_pending ?? 0} color="red" />
            <MiniStat to={sitesAtStage('report_submission_pending')} label="Report Submission Pending" value={pendingWork.group_stage?.report_submission_pending ?? 0} color="green" />
          </div>
          {pendingWork.by_workflow_status && (
            <div className="mt-4 h-72">
              <BarChart
                labels={Object.keys(pendingWork.by_workflow_status).map((k) => PENDING_LABELS[k] ?? k)}
                datasets={[{ label: 'Sites', data: Object.values(pendingWork.by_workflow_status).map((v) => toNumber(v)) }]}
                horizontal
                showLegend={false}
              />
            </div>
          )}
        </SectionCard>
      )}

      {pendingWork && (pendingActivity.length > 0 || pendingProject.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {pendingActivity.length > 0 && (
            <SectionCard title="Pending Work — Activity Level" icon={FaProjectDiagram}>
              <DataTable columns={flatCols('Activity', PENDING_RAW_STATUSES, PENDING_RAW_LABELS)} data={pendingActivity} maxHeight="320px" emptyMessage="No pending sites" />
            </SectionCard>
          )}
          {pendingProject.length > 0 && (
            <SectionCard title="Pending Work — Project Level" icon={FaBuilding}>
              <DataTable columns={flatCols('Project', PENDING_RAW_STATUSES, PENDING_RAW_LABELS)} data={pendingProject} maxHeight="320px" emptyMessage="No pending sites" />
            </SectionCard>
          )}
        </div>
      )}

      {(pendingEngineer.length > 0 || pendingTc.length > 0 || pendingTa.length > 0 || pendingMis.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pendingEngineer.length > 0 && (
            <SectionCard title="Pending — Engineer Level" icon={FaMapMarkerAlt}>
              <DataTable columns={flatCols('Engineer', ['engineer_assigned', 'field_work_in_progress', 'data_uploaded', 'field_work_completed'], { engineer_assigned: 'Engineer Assigned', field_work_in_progress: 'Field Work In Progress', data_uploaded: 'Site Data WIP', field_work_completed: 'Field Data Upload Pending' })} data={pendingEngineer} maxHeight="260px" emptyMessage="None" />
            </SectionCard>
          )}
          {pendingTc.length > 0 && (
            <SectionCard title="Pending — TC Level" icon={FaUserTie}>
              <DataTable columns={flatCols('TC', ['site_created', 'ta_review_pending'], { site_created: 'Site Created', ta_review_pending: 'TA Review Pending' })} data={pendingTc} maxHeight="260px" emptyMessage="None" />
            </SectionCard>
          )}
          {pendingTa.length > 0 && (
            <SectionCard title="Pending — TA Level" icon={FaUserTie}>
              <DataTable columns={flatCols('TA', ['ta_review_pending'], { ta_review_pending: 'TA Review Pending' })} data={pendingTa} maxHeight="260px" emptyMessage="None" />
            </SectionCard>
          )}
          {pendingMis.length > 0 && (
            <SectionCard title="Pending — MIS Level" icon={FaUserTie}>
              <DataTable columns={flatCols('MIS', ['mis_report_assign_pending', 'report_in_progress', 'report_completed'], { mis_report_assign_pending: 'MIS Report Assign Pending', report_in_progress: 'Report In Progress', report_completed: 'Validation Pending' })} data={pendingMis} maxHeight="260px" emptyMessage="None" />
            </SectionCard>
          )}
        </div>
      )}

      {/* Today / Yesterday */}
      {renderProgress(todayProgress, "Today's Progress", FaCheckDouble)}
      {renderProgress(yesterdayProductivity, 'Productivity (Yesterday)', FaCalendarCheck)}

      {/* Rejection Pending */}
      {rejectionPending && (
        <SectionCard title="Rejection Pending — Workflow Stage" icon={FaExclamationTriangle}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MiniStat label="TA Rejected" value={rejectionPending.by_workflow_status?.ta_rejected ?? 0} color="red" />
            <MiniStat label="Report Maker Rejected" value={rejectionPending.by_workflow_status?.report_maker_rejected ?? 0} color="red" />
            <MiniStat label="Validation Rejected" value={rejectionPending.by_workflow_status?.validation_rejected ?? 0} color="red" />
          </div>
        </SectionCard>
      )}

      {rejectionPending && (rejectActivity.length > 0 || rejectProject.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {rejectActivity.length > 0 && (
            <SectionCard title="Rejections — Activity Level" icon={FaProjectDiagram}>
              <DataTable columns={flatCols('Activity', REJECT_STATUSES, REJECT_LABELS)} data={rejectActivity} maxHeight="280px" emptyMessage="No rejections" />
            </SectionCard>
          )}
          {rejectProject.length > 0 && (
            <SectionCard title="Rejections — Project Level" icon={FaBuilding}>
              <DataTable columns={flatCols('Project', REJECT_STATUSES, REJECT_LABELS)} data={rejectProject} maxHeight="280px" emptyMessage="No rejections" />
            </SectionCard>
          )}
        </div>
      )}

      {rejectionPending?.user_level && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SectionCard title="Report Makers — Rejected" icon={FaUserTie}>
            <DataTable columns={[{ key: 'report_designer_username', label: 'Report Maker' }, { key: 'count', label: 'Rejected', align: 'center' }]} data={rejectionPending.user_level.report_maker_wise ?? []} maxHeight="240px" emptyMessage="None" />
          </SectionCard>
          <SectionCard title="Validators — Rejected" icon={FaUserTie}>
            <DataTable columns={[{ key: 'validator_username', label: 'Validator' }, { key: 'count', label: 'Rejected', align: 'center' }]} data={rejectionPending.user_level.validator_wise ?? []} maxHeight="240px" emptyMessage="None" />
          </SectionCard>
          <SectionCard title="TA — Rejected" icon={FaUserTie}>
            <DataTable columns={[{ key: 'ta_username', label: 'TA' }, { key: 'count', label: 'Rejected', align: 'center' }]} data={rejectionPending.user_level.ta_wise ?? []} maxHeight="240px" emptyMessage="None" />
          </SectionCard>
        </div>
      )}

      {/* Reference lists */}
      {referenceLists && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
          <SectionCard title="Projects" icon={FaProjectDiagram}>
            <DataTable columns={[{ key: 'project_short_name', label: 'Code' }, { key: 'project_name', label: 'Project' }]} data={referenceLists.project_names ?? []} maxHeight="220px" emptyMessage="No projects" />
          </SectionCard>
          <SectionCard title="TAs" icon={FaUserTie}>
            <DataTable columns={[{ key: 'username', label: 'TA' }]} data={referenceLists.ta_names ?? []} maxHeight="220px" emptyMessage="No TAs" />
          </SectionCard>
          <SectionCard title="MIS" icon={FaUserTie}>
            <DataTable columns={[{ key: 'username', label: 'MIS' }]} data={referenceLists.mis_names ?? []} maxHeight="220px" emptyMessage="No MIS" />
          </SectionCard>
          <SectionCard title="Engineers" icon={FaMapMarkerAlt}>
            <DataTable columns={[{ key: 'username', label: 'Engineer' }]} data={referenceLists.engineer_names ?? []} maxHeight="220px" emptyMessage="No engineers" />
          </SectionCard>
        </div>
      )}
    </div>
  );
};

export default ProjectManagerSpecSections;